import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Star, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ReviewExchangePage() {
  const { id } = useParams(); // exchange_id
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [reason, setReason] = useState('');
  const [reviewee, setReviewee] = useState(null);
  const [exchange, setExchange] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    checkCanReview();
  }, [id, isAuthenticated]);

  const checkCanReview = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/exchanges/${id}/can-review`);
      setCanReview(response.data.can_review);
      setReason(response.data.reason || '');
      setReviewee(response.data.reviewee);
      setExchange(response.data.exchange);
    } catch (error) {
      console.error('Error checking review eligibility:', error);
      toast.error('Erreur lors de la vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Veuillez selectionner une note');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews', {
        exchange_id: id,
        rating,
        comment: comment || null,
      });
      setSuccess(true);
      toast.success('Avis envoyé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-lg">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        <Card className="rounded-2xl">
          <CardContent className="p-6">
            {!canReview ? (
              <div className="text-center py-4">
                <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Impossible de laisser un avis</h2>
                <p className="text-muted-foreground mb-6">{reason}</p>
                <Link to="/dashboard">
                  <Button variant="outline">Retour au tableau de bord</Button>
                </Link>
              </div>
            ) : success ? (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Merci pour votre avis !</h2>
                <p className="text-muted-foreground mb-6">
                  Votre avis aide la communauté à faire de meilleurs échanges.
                </p>
                <Link to="/dashboard">
                  <Button className="btn-primary">Retour au tableau de bord</Button>
                </Link>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary/10 mb-4">
                    <Star className="w-7 h-7 text-secondary" />
                  </div>
                  <h1 className="text-xl font-bold mb-1">Laisser un avis</h1>
                  <p className="text-sm text-muted-foreground">
                    Comment s&apos;est passe votre échange ?
                  </p>
                </div>

                {/* Reviewee info */}
                {reviewee && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 mb-6">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={reviewee.avatar_url} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {reviewee.full_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{reviewee.full_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {reviewee.location || 'Membre Oknî'}
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Star Rating */}
                  <div className="space-y-2">
                    <Label className="text-center block">Note</Label>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="p-1 transition-transform hover:scale-110"
                          data-testid={`star-${star}`}
                        >
                          <Star
                            className={`w-10 h-10 transition-colors ${
                              star <= (hoverRating || rating)
                                ? 'fill-secondary text-secondary'
                                : 'text-muted-foreground/30'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <p className="text-center text-sm text-muted-foreground">
                      {rating === 0 && 'Cliquez sur une etoile'}
                      {rating === 1 && 'Tres insatisfait'}
                      {rating === 2 && 'Insatisfait'}
                      {rating === 3 && 'Correct'}
                      {rating === 4 && 'Satisfait'}
                      {rating === 5 && 'Tres satisfait'}
                    </p>
                  </div>

                  {/* Comment */}
                  <div className="space-y-2">
                    <Label htmlFor="comment">Commentaire (optionnel)</Label>
                    <Textarea
                      id="comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Partagez votre experience..."
                      className="rounded-xl min-h-[100px]"
                      data-testid="review-comment"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    className="w-full btn-primary h-11"
                    disabled={submitting || rating === 0}
                    data-testid="submit-review-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Star className="w-4 h-4 mr-2" />
                        Envoyer mon avis
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
